import React, { useEffect, useRef, useState } from 'react';
import './AISkinLoader.css';

const ANALYSIS_STATUS_STEPS = [
  'Uploading your photo securely…',
  'Scanning facial features…',
  'Detecting skin conditions…',
  'Calculating your skin score…',
  'Preparing your results…',
];

const getCoverLayout = (image, containerWidth, containerHeight) => {
  const imageAspectRatio = image.width / image.height;
  const containerAspectRatio = containerWidth / containerHeight;

  let drawWidth = containerWidth;
  let drawHeight = containerHeight;

  if (imageAspectRatio > containerAspectRatio) {
    drawHeight = containerHeight;
    drawWidth = drawHeight * imageAspectRatio;
  } else {
    drawWidth = containerWidth;
    drawHeight = drawWidth / imageAspectRatio;
  }

  return {
    drawWidth,
    drawHeight,
    offsetX: (containerWidth - drawWidth) / 2,
    offsetY: (containerHeight - drawHeight) / 2,
  };
};

const drawImageCover = (ctx, image, containerWidth, containerHeight) => {
  const { drawWidth, drawHeight, offsetX, offsetY } = getCoverLayout(
    image,
    containerWidth,
    containerHeight
  );
  ctx.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);
};

/**
 * Scan frame for the loader overlay.
 * Captured images are already face-cropped, so video-space landmarks must not
 * be mapped directly — use a cover-aligned inset that frames the visible face.
 */
const resolveScanRegion = (_landmarks, _image, canvasW, canvasH) => ({
  x: canvasW * 0.06,
  y: canvasH * 0.05,
  width: canvasW * 0.88,
  height: canvasH * 0.9,
});

const AISkinLoader = ({ imageSrc, landmarks = null, progressMessage = null, requestStatus = null }) => {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const timeRef = useRef(0);
  const lastShuffleRef = useRef(0);
  const dataPointsRef = useRef([]);
  const landmarksRef = useRef(landmarks);
  const [statusIndex, setStatusIndex] = useState(0);

  useEffect(() => {
    landmarksRef.current = landmarks;
  }, [landmarks]);

  // Initialize data points for "high-tech" look (positions shuffled during animation)
  useEffect(() => {
    dataPointsRef.current = Array.from({ length: 15 }, () => ({
      x: Math.random(),
      y: Math.random(),
      size: Math.random() * 3 + 1,
      speed: Math.random() * 0.05 + 0.01,
      opacity: Math.random()
    }));
  }, []);

  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const displayImgRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const frameEl = containerRef.current;
    if (!canvas || !imageSrc) return;

    const syncCanvasSize = () => {
      const target = frameEl || displayImgRef.current;
      if (!target) return;
      const displayedWidth = Math.max(1, Math.round(target.clientWidth));
      const displayedHeight = Math.max(1, Math.round(target.clientHeight));
      if (canvas.width !== displayedWidth || canvas.height !== displayedHeight) {
        canvas.width = displayedWidth;
        canvas.height = displayedHeight;
      }
      canvas.style.width = '100%';
      canvas.style.height = '100%';
    };

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      syncCanvasSize();
      imageRef.current = img;
    };
    img.src = imageSrc;

    const resizeObserver = new ResizeObserver(syncCanvasSize);
    if (frameEl) resizeObserver.observe(frameEl);
    if (displayImgRef.current) resizeObserver.observe(displayImgRef.current);

    return () => {
      if (img.onload) img.onload = null;
      resizeObserver.disconnect();
    };
  }, [imageSrc]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const checkAndAnimate = () => {
      if (!imageRef.current || canvas.width === 0 || canvas.height === 0) {
        setTimeout(checkAndAnimate, 50);
        return;
      }

      const ctx = canvas.getContext('2d');

      const animate = () => {
        timeRef.current += 0.016;
        const t = timeRef.current;
        const width = canvas.width;
        const height = canvas.height;
        const image = imageRef.current;

        ctx.clearRect(0, 0, width, height);
        if (image) {
          drawImageCover(ctx, image, width, height);
        }

        const region = resolveScanRegion(landmarksRef.current, image, width, height);
        const bracketSize = Math.max(14, Math.min(region.width, region.height) * 0.085);
        const bracketWeight = Math.max(2, bracketSize * 0.12);
        const gridStep = Math.max(24, Math.min(region.width, region.height) * 0.12);

        ctx.save();
        ctx.globalCompositeOperation = 'source-over';

        const scanSpeed = 0.25;
        const scanProgress = (t * scanSpeed) % 2;
        const scanY = scanProgress <= 1
          ? region.y + (scanProgress * region.height)
          : region.y + ((2 - scanProgress) * region.height);

        const neonCyan = { r: 0, g: 255, b: 255 };
        const neonCyanStr = `rgba(${neonCyan.r}, ${neonCyan.g}, ${neonCyan.b}, 1)`;

        ctx.save();
        ctx.strokeStyle = `rgba(${neonCyan.r}, ${neonCyan.g}, ${neonCyan.b}, 0.1)`;
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        for (let x = region.x; x <= region.x + region.width; x += gridStep) {
          ctx.moveTo(x, region.y);
          ctx.lineTo(x, region.y + region.height);
        }
        for (let y = region.y; y <= region.y + region.height; y += gridStep) {
          ctx.moveTo(region.x, y);
          ctx.lineTo(region.x + region.width, y);
        }
        ctx.stroke();
        ctx.restore();

        ctx.strokeStyle = neonCyanStr;
        ctx.lineWidth = bracketWeight;
        ctx.lineJoin = 'miter';

        ctx.beginPath();
        ctx.moveTo(region.x, region.y + bracketSize);
        ctx.lineTo(region.x, region.y);
        ctx.lineTo(region.x + bracketSize, region.y);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(region.x + region.width - bracketSize, region.y);
        ctx.lineTo(region.x + region.width, region.y);
        ctx.lineTo(region.x + region.width, region.y + bracketSize);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(region.x, region.y + region.height - bracketSize);
        ctx.lineTo(region.x, region.y + region.height);
        ctx.lineTo(region.x + bracketSize, region.y + region.height);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(region.x + region.width - bracketSize, region.y + region.height);
        ctx.lineTo(region.x + region.width, region.y + region.height);
        ctx.lineTo(region.x + region.width, region.y + region.height - bracketSize);
        ctx.stroke();

        const shuffleInterval = 1.4;
        if (t - lastShuffleRef.current >= shuffleInterval) {
          lastShuffleRef.current = t;
          dataPointsRef.current.forEach(pt => {
            pt.x = Math.random();
            pt.y = Math.random();
          });
        }
        ctx.save();
        dataPointsRef.current.forEach(pt => {
          pt.opacity = (Math.sin(t * 5 + pt.x * 10) + 1) / 2;
          ctx.fillStyle = `rgba(${neonCyan.r}, ${neonCyan.g}, ${neonCyan.b}, ${pt.opacity * 0.6})`;
          const px = region.x + pt.x * region.width;
          const py = region.y + pt.y * region.height;
          ctx.beginPath();
          ctx.arc(px, py, pt.size, 0, Math.PI * 2);
          ctx.fill();

          if (pt.size > 3) {
            ctx.strokeStyle = `rgba(${neonCyan.r}, ${neonCyan.g}, ${neonCyan.b}, ${pt.opacity * 0.4})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(px - 5, py); ctx.lineTo(px + 5, py);
            ctx.moveTo(px, py - 5); ctx.lineTo(px, py + 5);
            ctx.stroke();
          }
        });
        ctx.restore();

        const scanLineHeight = 2;
        const verticalFadeDistance = Math.max(18, region.height * 0.04);

        const verticalGradient = ctx.createLinearGradient(0, scanY - verticalFadeDistance, 0, scanY + verticalFadeDistance);
        verticalGradient.addColorStop(0, `rgba(${neonCyan.r}, ${neonCyan.g}, ${neonCyan.b}, 0)`);
        verticalGradient.addColorStop(0.3, `rgba(${neonCyan.r}, ${neonCyan.g}, ${neonCyan.b}, 0.5)`);
        verticalGradient.addColorStop(0.5, `rgba(${neonCyan.r}, ${neonCyan.g}, ${neonCyan.b}, 1)`);
        verticalGradient.addColorStop(0.7, `rgba(${neonCyan.r}, ${neonCyan.g}, ${neonCyan.b}, 0.5)`);
        verticalGradient.addColorStop(1, `rgba(${neonCyan.r}, ${neonCyan.g}, ${neonCyan.b}, 0)`);

        ctx.fillStyle = verticalGradient;
        ctx.fillRect(region.x, scanY - verticalFadeDistance, region.width, verticalFadeDistance * 2);

        ctx.strokeStyle = `rgba(${neonCyan.r}, ${neonCyan.g}, ${neonCyan.b}, 1)`;
        ctx.lineWidth = scanLineHeight;
        ctx.lineCap = 'square';
        ctx.shadowBlur = 8;
        ctx.shadowColor = `rgba(${neonCyan.r}, ${neonCyan.g}, ${neonCyan.b}, 0.8)`;

        ctx.beginPath();
        ctx.moveTo(region.x, scanY);
        ctx.lineTo(region.x + region.width, scanY);
        ctx.stroke();
        ctx.shadowBlur = 0;

        ctx.restore();

        animationFrameRef.current = requestAnimationFrame(animate);
      };

      animate();
    };

    checkAndAnimate();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [imageSrc]);

  useEffect(() => {
    const lastStatusIndex = ANALYSIS_STATUS_STEPS.length - 1;
    const intervalId = setInterval(() => {
      setStatusIndex(prev => {
        if (prev >= lastStatusIndex) {
          return prev;
        }
        return prev + 1;
      });
    }, 2600);

    return () => clearInterval(intervalId);
  }, []);

  return (
    <div className="ny-skin-loader">
      <div className="ny-skin-loader__frame" ref={containerRef}>
        {imageSrc && (
          <img
            ref={displayImgRef}
            src={imageSrc}
            alt=""
            aria-hidden="true"
            className="ny-skin-loader__image"
          />
        )}
        <canvas
          ref={canvasRef}
          className="ny-skin-loader__canvas"
        />
      </div>
      <div className="ny-skin-loader__status" aria-live="polite">
        {progressMessage || ANALYSIS_STATUS_STEPS[statusIndex]}
      </div>
    </div>
  );
};

export default AISkinLoader;

